import { useState } from "react";
import { MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import api, { formatApiError } from "@/lib/api";
import { track } from "@/lib/analytics";
import { Reveal } from "@/components/Reveal";

const darkInput = "w-full rounded-xl border border-white/20 bg-white/10 px-4 py-3.5 text-white placeholder-white/40 outline-none transition-colors focus:border-rose-300";

export default function LaunchTextCapture({ page = "" }) {
  const [form, setForm] = useState({ first_name: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/launch-text", { ...form, page });
      track("launch_text_signup", { page });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="bg-cream py-14 sm:py-16" data-testid="launch-text-section">
      <div className="mx-auto max-w-3xl px-5 sm:px-8">
        <Reveal>
          <div className="grain relative overflow-hidden rounded-3xl bg-navy p-8 text-center text-white shadow-lift sm:p-12">
            <div className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-victory/20 blur-3xl" aria-hidden="true" />
            {done ? (
              <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} data-testid="launch-text-success">
                <h2 className="font-display text-2xl font-800 tracking-tight sm:text-3xl">YOU'RE ON THE LIST.</h2>
                <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-white/75">
                  We'll text you the moment Service #1 details are official. See you in January!
                </p>
              </motion.div>
            ) : (
              <div className="relative">
                <h2 className="text-balance font-display text-2xl font-800 tracking-tight sm:text-3xl">
                  NOT READY YET? GET A TEXT WHEN WE LAUNCH.
                </h2>
                <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-white/75">
                  One text when Service #1 details are official. That's it - no spam, ever.
                </p>
                <form onSubmit={submit} className="mx-auto mt-7 flex max-w-lg flex-col gap-3 sm:flex-row" data-testid="launch-text-form">
                  <label htmlFor="lt-name" className="sr-only">First name (optional)</label>
                  <input id="lt-name" data-testid="launch-text-name" className={darkInput} placeholder="First name (optional)"
                         value={form.first_name} onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))} autoComplete="given-name" />
                  <label htmlFor="lt-phone" className="sr-only">Mobile phone</label>
                  <input id="lt-phone" data-testid="launch-text-phone" required type="tel" className={darkInput} placeholder="Mobile phone"
                         value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} autoComplete="tel" />
                  <button type="submit" disabled={loading} data-testid="launch-text-submit"
                          className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-victory px-7 py-3.5 font-display text-sm font-700 text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-victory-dark disabled:opacity-60">
                    <MessageCircle size={16} aria-hidden="true" /> {loading ? "JOINING..." : "TEXT ME"}
                  </button>
                </form>
              </div>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
